
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useQuery } from "@tanstack/react-query";
import { getUserServiceProviderCount } from "@/services/providers/providerOwnerService";
import { isUnauthorizedPlatformApiError } from "@/services/platformApi/client";
import { useSubscription } from "@/hooks/useSubscription";
import { useTranslation } from "react-i18next";
import { trackHomepageEvent } from "@/services/homepageAnalytics";

const ProviderCTA = () => {
  const { t } = useTranslation('homepage');
  const { user, isAdmin, loading } = useAuth();
  const { data: subscriptionStatus } = useSubscription();

  // Query to get user's listings count if authenticated
  const { data: userListingsCount = 0 } = useQuery({
    queryKey: ['ctaUserListingsCount', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      
      try {
        return await getUserServiceProviderCount(user.id);
      } catch (error) {
        if (!isUnauthorizedPlatformApiError(error)) {
          console.error("Error fetching user listings count:", error);
        }
        return 0;
      }
    },
    enabled: !loading && !!user
  });

  // Check if user is on free plan and has reached max listings
  // Admin users should always be treated as having a subscription
  const hasReachedFreeLimit = user && !isAdmin && !subscriptionStatus?.subscribed && userListingsCount >= 1;

  // Determine button text and link target based on auth and subscription status
  const buttonText = !user 
    ? t('providerCta.button')
    : hasReachedFreeLimit 
      ? t('providerCta.upgradeButton')
      : t('providerCta.manageButton');
  
  const buttonLink = !user 
    ? "/auth?redirect=/submit-listing" 
    : hasReachedFreeLimit 
      ? "/pricing" 
      : "/submit-listing";

  return (
    <section className="py-16 md:py-20 bg-dark-surface">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl rounded-md border border-dark-border bg-dark-elevated p-8 text-center md:p-10">
          <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-dark-muted">
            {t('providerCta.eyebrow')}
          </p>
          <h2
            className="mb-4 font-heading text-[length:var(--text-h2)] font-bold text-white"
          >
            {t('providerCta.title')}
          </h2>
          <p className="mx-auto mb-6 max-w-2xl text-[length:var(--text-body)] text-dark-muted">
            {t('providerCta.description')}
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm">
            <span className="flex items-center gap-1.5 text-white">
              <span className="w-5 h-5 rounded-full bg-whatsapp/20 inline-flex items-center justify-center text-whatsapp text-xs">✓</span>
              {t('providerCta.benefit1')}
            </span>
            <span className="flex items-center gap-1.5 text-white">
              <span className="w-5 h-5 rounded-full bg-whatsapp/20 inline-flex items-center justify-center text-whatsapp text-xs">✓</span>
              {t('providerCta.benefit2')}
            </span>
            <span className="flex items-center gap-1.5 text-white">
              <span className="w-5 h-5 rounded-full bg-whatsapp/20 inline-flex items-center justify-center text-whatsapp text-xs">✓</span>
              {t('providerCta.benefit3')}
            </span>
          </div>
          <Link 
            to={buttonLink} 
            onClick={() => trackHomepageEvent('homepage_provider_signup_cta_click', {
              destination: buttonLink,
              authenticated: Boolean(user),
              reached_free_limit: Boolean(hasReachedFreeLimit),
            })}
            className="inline-block w-full rounded-md bg-whatsapp px-8 py-3.5 text-base font-medium text-white transition-colors hover:bg-whatsapp-hover sm:w-auto"
          >
            {buttonText}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProviderCTA;
